import React, { useEffect } from 'react';
import PricingSection from '../../components/Home/PricingSection';

const BusinessPlanLanding: React.FC = () => {
  // SEO per project policy (no external SEO libs)
  useEffect(() => {
    const title = 'AI Business Plan Generator | Explanatum';
    const desc = 'Our AI will create a detailed business plan in minutes. Get executive summary, market analysis, pricing, roadmap, and financials—auto‑generated from your inputs.';
    const url = 'https://explanatum.co.uk/modules/business-plan';
    document.title = title;

    // Basic meta update helper
    const setMeta = (name: string, content: string) => {
      let el = document.querySelector(`meta[name="${name}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute('name', name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    const setOg = (property: string, content: string) => {
      let el = document.querySelector(`meta[property="${property}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute('property', property);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    setMeta('description', desc);
    setOg('og:title', title);
    setOg('og:description', desc);
    setOg('og:url', url);

    // Canonical
    let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!link) {
      link = document.createElement('link');
      link.setAttribute('rel', 'canonical');
      document.head.appendChild(link);
    }
    link.setAttribute('href', url);
  }, []);

  const track = (event: string, params: Record<string, unknown> = {}) => {
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const w = window as any;
      if (w && typeof w.gtag === 'function') w.gtag('event', event, params);
    } catch {}
  };

  // FAQPage JSON-LD for rich results
  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: 'Why do I need a business plan?',
        acceptedAnswer: {
          '@type': 'Answer',
          text:
            'A business plan turns your idea into a clear strategy that aligns goals, audience, pricing, channels, and milestones. It helps you prioritize, secure buy-in from partners or investors, and operate with measurable targets.',
        },
      },
      {
        '@type': 'Question',
        name: 'How often should I update my business plan?',
        acceptedAnswer: {
          '@type': 'Answer',
          text:
            'Review your plan quarterly and after major changes—like new products, pricing shifts, or market movements. Frequent iteration keeps execution aligned with what is working now.',
        },
      },
      {
        '@type': 'Question',
        name: 'How accurate are the plans?',
        acceptedAnswer: {
          '@type': 'Answer',
          text:
            "They're generated from your inputs and our templates. Use them as a strong starting point and refine with your own data.",
        },
      },
      {
        '@type': 'Question',
        name: 'Do I need an account?',
        acceptedAnswer: {
          '@type': 'Answer',
          text:
            'You can try idea generation free daily. To generate a full plan and save it, start a 7-day trial.',
        },
      },
      {
        '@type': 'Question',
        name: 'Can I export?',
        acceptedAnswer: {
          '@type': 'Answer',
          text:
            'Yes, once generated you can export or copy sections into your own documents.',
        },
      },
    ],
  } as const;

  return (
    <main className="min-h-screen bg-gray-50" role="main">
      {/* Hero */}
      <section className="relative overflow-hidden bg-white text-gray-600" role="region" aria-label="Hero">
        <div className="max-w-7xl mx-auto px-6 pt-20 pb-24 lg:pt-28 lg:pb-32 relative">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <div>
              <span className="inline-flex items-center gap-2 rounded-full border border-blue-200 bg-blue-50 px-4 py-1 text-sm font-semibold text-blue-700">
                <span className="inline-flex h-2 w-2 rounded-full bg-blue-600" />
                AI Business Plan Generator
              </span>
              <h1 className="mt-6 text-4xl font-extrabold tracking-tight text-gray-900 sm:text-5xl lg:text-6xl">
                Build investor-ready plans in minutes.
                <span className="mt-2 block text-gray-900">
                  No spreadsheets, no guesswork.
                </span>
              </h1>
              <p className="mt-6 text-lg text-gray-600 max-w-xl">
                Capture your idea once and generate a complete strategy—market analysis, pricing, roadmap, and financial scaffolding—ready to share with stakeholders or update alongside execution.
              </p>
              <div className="mt-8 flex flex-wrap items-center gap-4">
                <a
                  href="#free-trial"
                  onClick={() => track('click_hero_cta', { page: 'business_plan_landing', placement: 'hero' })}
                  className="inline-flex items-center justify-center rounded-full bg-blue-600 hover:bg-blue-700 transition-colors px-6 py-3 text-base font-semibold text-white shadow-lg transition hover:scale-[1.02]"
                >
                  Start 7‑day trial
                </a>
                <a
                  href="#compare"
                  className="inline-flex items-center justify-center rounded-full border border-blue-600/50 px-6 py-3 text-base font-semibold text-gray-600 hover:bg-blue-50 transition-colors"
                >
                  See how it compares
                </a>
              </div>
              <div className="mt-8 flex flex-wrap items-center gap-4 text-sm text-gray-500">
                <span className="flex items-center gap-2">
                  <span className="inline-flex h-2 w-2 rounded-full bg-emerald-400" />
                  Structured for bank & investor reviews
                </span>
                <span className="flex items-center gap-2">
                  <span className="inline-flex h-2 w-2 rounded-full bg-sky-400" />
                  Auto-builds SWOT, TAM, and forecasts
                </span>
                <span className="flex items-center gap-2">
                  <span className="inline-flex h-2 w-2 rounded-full bg-blue-600" />
                  Syncs with the Business Consultant
                </span>
              </div>
            </div>
            <aside className="relative" aria-label="Table of contents">
              <div className="absolute -top-6 -left-6 h-32 w-32 rounded-full bg-blue-50 blur-3xl" aria-hidden="true" />
              <div className="absolute -bottom-10 -right-8 h-36 w-36 rounded-full bg-cyan-50 blur-3xl" aria-hidden="true" />
              <nav className="relative rounded-3xl border border-gray-200 bg-white shadow-2xl " aria-labelledby="bp-toc-title">
                <div className="px-6 py-5 border-b border-gray-200">
                  <h2 id="bp-toc-title" className="text-lg font-semibold text-gray-900">Business plan blueprint</h2>
                  <p className="text-sm text-gray-500 mt-1">Every section your stakeholders expect—auto-generated and ready to edit.</p>
                </div>
                <ol className="p-6 space-y-2 list-decimal list-inside text-gray-600">
                  <li><a href="#bp-business-overview" className="hover:text-sky-700">Business overview</a></li>
                  <li><a href="#bp-executive-summary" className="hover:text-sky-700">Executive summary</a></li>
                  <li><a href="#bp-swot" className="hover:text-sky-700">SWOT analysis</a></li>
                  <li><a href="#bp-business-models" className="hover:text-sky-700">Business models</a></li>
                  <li><a href="#bp-viability" className="hover:text-sky-700">Viability & validation</a></li>
                  <li><a href="#bp-market-research" className="hover:text-sky-700">Market research & TAM</a></li>
                  <li><a href="#bp-products-services" className="hover:text-sky-700">Products & services</a></li>
                  <li><a href="#bp-sales-marketing" className="hover:text-sky-700">Sales & marketing</a></li>
                  <li><a href="#bp-operations" className="hover:text-sky-700">Operations plan</a></li>
                  <li><a href="#bp-financials" className="hover:text-sky-700">Financial forecasts</a></li>
                  <li><a href="#bp-implementation" className="hover:text-sky-700">Implementation roadmap</a></li>
                </ol>
              </nav>
            </aside>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="max-w-7xl mx-auto px-4 py-12" role="region" aria-label="Features">
        <h2 className="text-2xl font-semibold text-gray-900">What you get</h2>
        <div className="mt-6 grid md:grid-cols-3 gap-6">
          <div className="rounded-xl border border-gray-200 bg-white p-5">
            <h3 className="font-medium text-gray-900">Customer & Market</h3>
            <p className="text-gray-700 mt-2">ICP, pain points, buying triggers, competitors, and differentiation.</p>
          </div>
          <div className="rounded-xl border border-gray-200 bg-white p-5">
            <h3 className="font-medium text-gray-900">Offers & Pricing</h3>
            <p className="text-gray-700 mt-2">Validated offers, price bands, and packaging guidance by model.</p>
          </div>
          <div className="rounded-xl border border-gray-200 bg-white p-5">
            <h3 className="font-medium text-gray-900">Roadmap & GTM</h3>
            <p className="text-gray-700 mt-2">90‑day roadmap with channels, messaging, and next actions.</p>
          </div>
        </div>
      </section>

      {/* Comparison */}
      <section id="compare" aria-label="Comparison" className="relative overflow-hidden bg-white py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="text-center">
            <h2 className="text-3xl font-bold text-gray-900 sm:text-4xl">Why founders choose it over legacy plan tools</h2>
            <p className="mt-4 text-base text-gray-500">We deliver investor-grade plans that stay in sync with execution. Traditional software and template packs can’t keep pace with live data.</p>
          </div>
          <div className="mt-14 grid gap-6 md:grid-cols-3">
            {[{
              title: 'AI Business Plan Generator',
              points: [
                'Tailored plan outline driven by your industry and goals',
                'Pulls through market sizing, SWOT, pricing, and roadmap automatically',
                'Exports to PDF/Docx and syncs with the Business Consultant module',
                'Tracks milestones and updates as you refine assumptions',
                'Built-in prompts to gather the right data before drafting'
              ],
              accent: 'border-gray-200 bg-gray-50 text-white'
            }, {
              title: 'LivePlan & SBA templates',
              points: [
                'Static outlines require manual editing in Word or PowerPoint',
                'Limited guidance on SWOT, TAM, or pricing without extra research',
                'No link to CRM metrics or financial tools for validation',
                'Hard to collaborate—versions live in email attachments',
                'Updating assumptions means rebuilding charts by hand'
              ],
              accent: 'border-gray-200 bg-gray-50 text-white'
            }, {
              title: 'Bizplan, Upmetrics & similar',
              points: [
                'Focus on static templates with light editing rather than AI insight',
                'Financial models depend on manual spreadsheet work',
                'No automated prompts to capture unique positioning or GTM',
                'Separate tools needed for execution tracking after plan approval',
                'Collaboration limited to comments—no shared context with consultants'
              ],
              accent: 'border-gray-200 bg-gray-50 text-white'
            }].map((column) => (
              <article key={column.title} className={`rounded-3xl border ${column.accent} p-6 shadow-xl shadow-lg`}>
                <h3 className="text-lg font-semibold text-gray-900">{column.title}</h3>
                <ul className="mt-5 space-y-3 text-sm text-gray-900">
                  {column.points.map((point) => (
                    <li key={point} className="flex items-start gap-3 text-gray-900">
                      <span className="mt-1 inline-flex h-1.5 w-1.5 rounded-full bg-white" />
                      <span className="text-gray-900">{point}</span>
                    </li>
                  ))}
                </ul>
              </article>
            ))}
          </div>
          <div className="mt-14 rounded-3xl border border-indigo-400/30 bg-indigo-400/10 p-8 text-center ">
            <h3 className="text-2xl font-semibold text-gray-900">“Can you assemble a full business plan draft from my idea brief?”</h3>
            <p className="mt-3 text-base text-gray-600">Our generator captures your inputs, builds every section, and keeps them synced so you can iterate with investors and teammates.</p>
            <div className="mt-6 flex flex-wrap justify-center gap-4">
              <a
                href="#free-trial"
                onClick={() => track('click_compare_cta', { page: 'business_plan_landing' })}
                className="inline-flex items-center justify-center rounded-full bg-gradient-to-r from-indigo-500 via-blue-600 to-sky-500 px-6 py-3 text-base font-semibold text-gray-900 shadow-lg shadow-indigo-500/30 hover:scale-[1.02]"
              >
                Generate my plan now
              </a>
              <a
                href="/modules/business-consultant"
                className="inline-flex items-center justify-center rounded-full border border-blue-600/60 px-6 py-3 text-base font-semibold text-gray-600 hover:border-slate-300/70"
              >
                Pair with the Consultant
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Semantic anchor sections to match TOC for SEO + accessibility */}
      <article className="max-w-7xl mx-auto px-4 pb-8" aria-label="Business plan sections">
        <div className="grid md:grid-cols-2 gap-8">
          <section id="bp-business-overview" className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
            <h2 className="text-xl font-semibold text-gray-900">Business Overview</h2>
            <p className="text-gray-700 mt-2">High-level description of your company, audience, and value proposition. This section clarifies what you do and why it matters, aligning all stakeholders on the problem you solve and the outcomes you create.</p>
          </section>
          <section id="bp-executive-summary" className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
            <h2 className="text-xl font-semibold text-gray-900">Executive Summary</h2>
            <p className="text-gray-700 mt-2">Concise overview of goals, model, traction, and key milestones. It’s the first impression for investors and partners, highlighting why the opportunity is compelling right now.</p>
          </section>
          <section id="bp-swot" className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
            <h2 className="text-xl font-semibold text-gray-900">SWOT Analysis</h2>
            <p className="text-gray-700 mt-2">Clear view of strengths, weaknesses, opportunities, and threats. Understanding internal and external factors helps you prioritize initiatives and de‑risk execution.</p>
          </section>
          <section id="bp-business-models" className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
            <h2 className="text-xl font-semibold text-gray-900">Business Models</h2>
            <p className="text-gray-700 mt-2">Evaluation of viable revenue models such as productized services, SaaS, and subscriptions. Choosing the right model is critical for margins, scalability, and investor fit.</p>
          </section>
          <section id="bp-viability" className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
            <h2 className="text-xl font-semibold text-gray-900">Viability Analysis</h2>
            <p className="text-gray-700 mt-2">Assesses feasibility, risks, assumptions, and validation steps. This ensures you test what matters first and allocate resources to the highest‑impact experiments.</p>
          </section>
          <section id="bp-market-research" className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
            <h2 className="text-xl font-semibold text-gray-900">Market Research</h2>
            <p className="text-gray-700 mt-2">Defines ICP, TAM/SAM/SOM, competitors, and positioning. Solid research informs pricing, messaging, and channel strategy so you reach the right buyers efficiently.</p>
          </section>
          <section id="bp-products-services" className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
            <h2 className="text-xl font-semibold text-gray-900">Products and Services</h2>
            <p className="text-gray-700 mt-2">Details your offers, benefits, and differentiation. Clear articulation of value helps prospects understand why your solution is the best choice.</p>
          </section>
          <section id="bp-sales-marketing" className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
            <h2 className="text-xl font-semibold text-gray-900">Sales and Marketing</h2>
            <p className="text-gray-700 mt-2">Outlines channels, messaging, funnel, and content plan. A focused GTM ensures repeatable customer acquisition and visibility in the markets that matter.</p>
          </section>
          <section id="bp-operations" className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
            <h2 className="text-xl font-semibold text-gray-900">Operations</h2>
            <p className="text-gray-700 mt-2">Covers delivery, tooling, processes, and staffing. Strong ops translate strategy into consistent execution, quality, and customer satisfaction.</p>
          </section>
          <section id="bp-financials" className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
            <h2 className="text-xl font-semibold text-gray-900">Financial Projections</h2>
            <p className="text-gray-700 mt-2">Summarizes pricing assumptions, revenue drivers, and forecast. Financial clarity guides budgeting, hiring, and investor discussions.</p>
          </section>
          <section id="bp-implementation" className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
            <h2 className="text-xl font-semibold text-gray-900">Implementation Plan</h2>
            <p className="text-gray-700 mt-2">Maps a 90‑day roadmap with milestones and KPIs. Clear ownership and timelines keep teams aligned and momentum high.</p>
          </section>
        </div>
      </article>

      {/* FAQs */}
      <section className="max-w-7xl mx-auto px-4 pb-12" role="region" aria-label="FAQs">
        <h2 className="text-2xl font-semibold text-gray-900">FAQs</h2>
        <div className="mt-6 space-y-4">
          <details className="rounded-lg border border-gray-200 bg-white p-4">
            <summary className="font-medium text-gray-900 cursor-pointer">Why do I need a business plan?</summary>
            <p className="text-gray-700 mt-2">A business plan translates your idea into an actionable strategy with clear goals, positioning, and a path to market. It aligns stakeholders, surfaces risks, and helps you prioritize the next most impactful steps.</p>
          </details>
          <details className="rounded-lg border border-gray-200 bg-white p-4">
            <summary className="font-medium text-gray-900 cursor-pointer">How often should I update my business plan?</summary>
            <p className="text-gray-700 mt-2">Review it quarterly and after major changes such as new products, pricing updates, or market shifts. Iterating frequently keeps execution aligned with what is working now and prevents drift.</p>
          </details>
          <details className="rounded-lg border border-gray-200 bg-white p-4">
            <summary className="font-medium text-gray-900 cursor-pointer">How accurate are the plans?</summary>
            <p className="text-gray-700 mt-2">They’re generated from your inputs and our templates. Use them as a strong starting point and refine with your own data.</p>
          </details>
          <details className="rounded-lg border border-gray-200 bg-white p-4">
            <summary className="font-medium text-gray-900 cursor-pointer">Do I need an account?</summary>
            <p className="text-gray-700 mt-2">You can try idea generation free daily. To generate a full plan and save it, start a 7‑day trial.</p>
          </details>
          <details className="rounded-lg border border-gray-200 bg-white p-4">
            <summary className="font-medium text-gray-900 cursor-pointer">Can I export?</summary>
            <p className="text-gray-700 mt-2">Yes, once generated you can export or copy sections to your documents.</p>
          </details>
        </div>
      </section>
      {/* JSON-LD for FAQ rich results */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }} />

      {/* Signup at bottom only */}
      <section aria-labelledby="free-trial" id="free-trial" className="max-w-7xl mx-auto px-4 pb-16 pt-6">
        <PricingSection />
      </section>

    </main>
  );
};

export default BusinessPlanLanding;
